import { h } from 'preact';
import { useState } from 'preact/hooks';
import { createElement } from 'lucide';
import * as icons from 'lucide';
import type { ViewPreset } from '../../../types';

interface ViewPresetsDropdownProps {
  presets: ViewPreset[];
  activePresetId: string | null;
  onViewSnap: (presetId: string) => void;
}

export const ViewPresetsDropdown = ({ presets, activePresetId, onViewSnap }: ViewPresetsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Group presets
  const groups: Record<string, ViewPreset[]> = {
    vertical: [],
    sides: [],
    corners: [],
    custom: [],
  };

  for (const preset of presets) {
    const group = preset.group || 'custom';
    if (groups[group]) {
      groups[group].push(preset);
    } else {
      groups.custom.push(preset);
    }
  }

  const viewIconMap: Record<string, string> = {
    top: 'ArrowUp',
    bottom: 'ArrowDown',
    front: 'ArrowUpRight',
    back: 'ArrowDownLeft',
    left: 'ArrowLeft',
    right: 'ArrowRight',
    'corner-fr': 'CornerUpRight',
    'corner-fl': 'CornerUpLeft',
    'corner-br': 'CornerDownRight',
    'corner-bl': 'CornerDownLeft',
  };

  const groupLabels: Record<string, string> = {
    vertical: 'Top / Bottom',
    sides: 'Sides',
    corners: 'Corners',
    custom: 'Custom',
  };

  const renderIcon = (iconName: string, size = 18): h.JSX.Element => {
    const icon = (icons as any)[iconName] ?? icons.Camera;
    const svg = createElement(icon, {
      width: size,
      height: size,
      stroke: 'currentColor',
      'stroke-width': 1.8,
    });
    return <span dangerouslySetInnerHTML={{ __html: svg.outerHTML }} />;
  };

  const activePreset = presets.find(p => p.id === activePresetId);

  return (
    <div class="visualizer-panel visualizer-viewsnaps-panel">
      <div class="visualizer-viewsnaps-dropdown">
        <button
          class="visualizer-viewsnaps-toggle"
          onClick={() => setIsOpen(!isOpen)}
        >
          {renderIcon('Camera')}
          <span class="visualizer-viewsnaps-label">
            {activePreset ? activePreset.name : 'Select View'}
          </span>
          {renderIcon('ChevronDown', 14)}
        </button>

        <div class={`visualizer-viewsnaps-menu ${isOpen ? 'open' : ''}`}>
          {['vertical', 'sides', 'corners', 'custom'].map(groupKey => {
            const groupPresets = groups[groupKey];
            if (groupPresets.length === 0) return null;

            return (
              <div key={groupKey} class="visualizer-viewsnaps-group">
                <div class="visualizer-viewsnaps-group-title">{groupLabels[groupKey]}</div>
                <div class={`visualizer-viewsnaps-list ${groupPresets.length <= 2 ? 'single-column' : ''}`}>
                  {groupPresets.map(preset => (
                    <button
                      key={preset.id}
                      class={`visualizer-viewsnap-btn ${activePresetId === preset.id ? 'active' : ''}`}
                      onClick={() => {
                        onViewSnap(preset.id);
                        setIsOpen(false);
                      }}
                    >
                      {preset.icon && renderIcon(viewIconMap[preset.icon] || 'Circle', 16)}
                      <span>{preset.name}</span>
                      {preset.hotkey && <span class="hotkey">{preset.hotkey}</span>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
